/**
 * Cliente da API com o token do Azure AD (spec 20261019-gestao-de-acessos, RF01).
 *
 * `apiFetch(url, opcoes)` é o `fetch` de sempre com `Authorization: Bearer <token>` quando há
 * token. Sem MSAL (testes) ou sem conta, a chamada sai idêntica ao `fetch`.
 *
 * Com `VITE_MSAL_API_SCOPE` configurado (depois que o app registration expõe
 * `api://<client id>/access_as_user`), manda o access token desse escopo. Sem ele — caso de
 * hoje — manda o **ID token** do `User.Read` (já consentido no login): o backend aceita o
 * client id puro como audiência (`ACESSOS_AUDIENCES`), que é exatamente o `aud` do ID token v2,
 * então a identidade do usuário já é validável sem depender do app registration. É um degrau
 * abaixo do RF01 (token de API de verdade), mas evita a gestão de acessos ficar sem nenhuma
 * identidade em rotas `sempre=True` até alguém configurar o Entra ID.
 *
 * O token fica em memória até um minuto antes de expirar, e chamadas paralelas da mesma página
 * esperam a mesma busca. Uma falha suspende novas tentativas por alguns minutos: sem isso, um
 * escopo mal configurado faria cada chamada da API esperar uma ida ao Azure AD.
 */
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { apiScope } from '../authConfig';

const MARGEM_MS = 60 * 1000;
const PAUSA_APOS_FALHA_MS = 5 * 60 * 1000;

let instancia = null;
let escopo = apiScope;
let guardado = null;      // { token, expira }
let emAndamento = null;   // Promise<string|null>
let falhouAte = 0;
let precisaInteracao = false;

export const configurarApiClient = (msal, escopoApi = apiScope) => {
    instancia = msal;
    escopo = escopoApi;
};

/** O último pedido de token falhou porque o Azure AD quer interação (consentimento, sessão). */
export const tokenPrecisaInteracao = () => precisaInteracao;

const conta = () => instancia?.getActiveAccount() || instancia?.getAllAccounts()?.[0] || null;

const buscarToken = async () => {
    const atual = conta();
    if (!atual) return null;
    try {
        const resposta = await instancia.acquireTokenSilent({ scopes: [escopo || 'User.Read'], account: atual });
        const token = escopo ? resposta.accessToken : resposta.idToken;
        const expira = resposta.expiresOn ? new Date(resposta.expiresOn).getTime() : Date.now() + 5 * 60 * 1000;
        guardado = { token, expira };
        precisaInteracao = false;
        return token;
    } catch (erro) {
        falhouAte = Date.now() + PAUSA_APOS_FALHA_MS;
        precisaInteracao = erro instanceof InteractionRequiredAuthError;
        if (import.meta.env.DEV) {
            console.warn('[api] sem token para a API; as chamadas seguem sem ele por alguns minutos.', erro);
        }
        return null;
    }
};

export const tokenDaApi = async () => {
    if (!instancia) return null;
    if (guardado && guardado.expira - Date.now() > MARGEM_MS) return guardado.token;
    if (Date.now() < falhouAte) return null;
    if (!emAndamento) {
        emAndamento = buscarToken().finally(() => { emAndamento = null; });
    }
    return emAndamento;
};

export const apiFetch = async (entrada, ...resto) => {
    const token = await tokenDaApi();
    if (!token) return fetch(entrada, ...resto);
    const opcoes = resto[0] || {};
    const cabecalhos = new Headers(opcoes.headers || undefined);
    cabecalhos.set('Authorization', `Bearer ${token}`);
    return fetch(entrada, { ...opcoes, headers: cabecalhos });
};

/** Leva o usuário ao Azure AD para consentir/renovar o acesso à API (volta para a mesma página). */
export const renovarAcessoApi = () => {
    if (!instancia) return Promise.resolve();
    return instancia.acquireTokenRedirect({ scopes: [escopo || 'User.Read'], account: conta() || undefined });
};

/** Só para testes: volta o módulo ao estado inicial. */
export const reiniciarApiClient = () => {
    instancia = null;
    escopo = apiScope;
    guardado = null;
    emAndamento = null;
    falhouAte = 0;
    precisaInteracao = false;
};
